import { getCookie, removeCookie } from '@/app/common/utils/cookieService';
import type {
  IntroCodeType,
  ValidateIntroCodeAffiliateData,
  ValidateIntroCodeData,
  ValidateIntroCodeResponse,
} from '../types/ordersApiTypes';

export const AFFILIATE_COOKIE_NAME = 'aff';

export function getAffiliateCookieCode(): string | null {
  const code = getCookie(AFFILIATE_COOKIE_NAME)?.trim();
  return code || null;
}

export function clearAffiliateCookie(): void {
  removeCookie(AFFILIATE_COOKIE_NAME, true);
}

const INTRO_CODE_BUSINESS_ERROR_CODES = new Set([
  4000, 4001, 4002, 4003, 4004, 4005, 4006, 4007,
]);

export function isValidIntroCodeResponse(
  response: ValidateIntroCodeResponse | null | undefined,
): response is ValidateIntroCodeResponse & {
  success: true;
  code: 200;
  data: ValidateIntroCodeData;
} {
  if (!response || response.success !== true || response.code !== 200) {
    return false;
  }

  const { data } = response;

  return (
    data != null &&
    typeof data === 'object' &&
    (data.code_type === 'affiliate' || data.code_type === 'discount')
  );
}

export function isApplicableAffiliateIntroCodeResponse(
  response: ValidateIntroCodeResponse | null | undefined,
): response is ValidateIntroCodeResponse & {
  success: true;
  code: 200;
  data: ValidateIntroCodeAffiliateData;
} {
  return (
    isValidIntroCodeResponse(response) &&
    response.data.code_type === 'affiliate' &&
    typeof response.data.code === 'string' &&
    response.data.code.trim().length > 0
  );
}

/** Round UP so the last three digits are zero (matches go_handler). */
export function roundUpToThousands(amount: number): number {
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  return Math.ceil(amount / 1000) * 1000;
}

/** Percent discount with round-up to thousands, capped at amount. */
export function calcPercentDiscountAmount(
  amount: number,
  percent: number,
): number {
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  if (!Number.isFinite(percent) || percent <= 0) return 0;
  const raw = (amount * percent) / 100;
  return Math.min(roundUpToThousands(raw), amount);
}

/** 10% line tax with integer truncation (matches go_handler). */
export function calcLineTax(netAmount: number): number {
  if (!Number.isFinite(netAmount) || netAmount <= 0) return 0;
  return Math.trunc((10 * netAmount) / 100);
}

export function getIntroCodePercent(
  data: ValidateIntroCodeData,
): number | null {
  if (data.code_type === 'affiliate') {
    const percent = Number(data.discount_percent);
    if (!Number.isFinite(percent) || percent <= 0) return null;
    return percent;
  }

  if (data.discount_type === 1) {
    const value = Number(data.discount_value);
    if (!Number.isFinite(value) || value <= 0) return null;
    return value;
  }

  return null;
}

export function isFixedAmountIntroCode(data: ValidateIntroCodeData): boolean {
  return data.code_type === 'discount' && data.discount_type === 2;
}

/**
 * Split intro/discount across plan + addons.
 * Percent: each line separately (round UP to thousands), then sum.
 * Fixed amount: deducted from plan only.
 */
export function getIntroCodeDiscountBreakdown(
  data: ValidateIntroCodeData,
  planAmount: number,
  addonAmounts: number[],
): { planDiscount: number; addonDiscounts: number[]; total: number } {
  const safePlan = Number.isFinite(planAmount) && planAmount > 0 ? planAmount : 0;
  const safeAddons = addonAmounts.map((a) =>
    Number.isFinite(a) && a > 0 ? a : 0,
  );

  const percent = getIntroCodePercent(data);
  if (percent != null) {
    const planDiscount = calcPercentDiscountAmount(safePlan, percent);
    const addonDiscounts = safeAddons.map((a) =>
      calcPercentDiscountAmount(a, percent),
    );
    const total =
      planDiscount + addonDiscounts.reduce((sum, d) => sum + d, 0);
    return { planDiscount, addonDiscounts, total };
  }

  if (isFixedAmountIntroCode(data)) {
    const value = Number(data.discount_value);
    const planDiscount =
      Number.isFinite(value) && value > 0
        ? Math.min(Math.round(value), safePlan)
        : 0;
    return {
      planDiscount,
      addonDiscounts: safeAddons.map(() => 0),
      total: planDiscount,
    };
  }

  return {
    planDiscount: 0,
    addonDiscounts: safeAddons.map(() => 0),
    total: 0,
  };
}

export function getIntroCodeDiscountAmount(
  data: ValidateIntroCodeData,
  orderAmount: number,
): number {
  if (!Number.isFinite(orderAmount) || orderAmount <= 0) return 0;

  const percent = getIntroCodePercent(data);
  if (percent != null) {
    return calcPercentDiscountAmount(orderAmount, percent);
  }

  if (isFixedAmountIntroCode(data)) {
    const value = Number(data.discount_value);
    if (!Number.isFinite(value) || value <= 0) return 0;
    return Math.min(Math.round(value), orderAmount);
  }

  return 0;
}

export function getIntroCodeDeductionLabel(codeType: IntroCodeType): string {
  return codeType === 'affiliate' ? 'تخفیف کد معرف' : 'تخفیف کد';
}

export function getIntroCodeWalletCreditLabel(codeType: IntroCodeType): string {
  return codeType === 'affiliate' ? 'اعتبار کد معرف' : 'اعتبار کد تخفیف';
}

export function getIntroCodeErrorMessage(
  payload: unknown,
  fallbackMessage: string,
): string {
  if (!payload || typeof payload !== 'object') return fallbackMessage;

  const body = payload as {
    code?: number;
    msg?: string | null;
    data?: unknown;
  };

  if (typeof body.msg === 'string' && body.msg.trim()) {
    return body.msg.trim();
  }

  if (
    typeof body.code === 'number' &&
    INTRO_CODE_BUSINESS_ERROR_CODES.has(body.code)
  ) {
    return fallbackMessage;
  }

  return fallbackMessage;
}

export function getIntroCodeErrorFromCatch(
  _error: unknown,
  fallbackMessage: string,
): string {
  // خطای HTTP غیر ۲۰۰؛ پیام ثابت خودش را نشان می‌دهیم، نه msg سرور
  return fallbackMessage;
}
